import {createAsyncThunk} from '@reduxjs/toolkit';
import {getJWTToken} from '../../lib/auth-token';
import {handleAuthFailure, signOutExpiredSession} from '../../lib/auth-error-handler';
import {getWorkerApiUrl, apiFetch} from '../../lib/api';
import {debugLog} from '../../lib/debug';
import {ApiError, toApiError, toNetworkError} from '../../lib/apiError';
import type {TranscriptionUsage, UsageResponse} from '../../backend-types/usage-api';

/**
 * The rate throttle, camelCased.
 *
 * Independent of the period balance: a job has to clear both, so having 20 hours left this period
 * does not mean 20 hours can be started now. `null` on the snapshot means the plan has no throttle
 * at all — which is not the same as one that is merely idle.
 *
 * **`isActive === false` means the clock has not started, not that a limit is in force.** Reading
 * usage does not open a session; only transcribing does. So there is no countdown to show and
 * nothing has been spent — `startedAt` and `expiresAt` are both null, and the full budget is
 * waiting. Rendering a timer here would invent a deadline the user is not under.
 */
export interface TranscriptionSessionSnapshot {
  isActive: boolean;
  secondsUsed: number;
  secondsLimit: number;
  secondsRemaining: number;
  /** How long a session lasts once opened. Not a countdown — see `expiresAt` for that. */
  durationSeconds: number;
  /** ISO 8601 UTC, null while inactive. */
  startedAt: string | null;
  /** ISO 8601 UTC, null while inactive. When the budget resets. */
  expiresAt: string | null;
}

/**
 * The server's transcription allowance — the same numbers it answers its 402s and 429s with, so the
 * client never derives a balance itself. The period is the user's billing cycle (anchored on their
 * Paddle renewal date, or their signup date if they have never paid), not the calendar month.
 * `secondsLimit`, `secondsRemaining` and `maxFileSeconds` are null when the plan is uncapped.
 *
 * Every number here is a `plan_limits` row, changeable without a deploy — none of it may be
 * hardcoded in the UI.
 */
export interface UsageSnapshot {
  plan: string;
  secondsUsed: number;
  secondsLimit: number | null;
  secondsRemaining: number | null;
  /** ISO 8601 start of the current billing period. */
  periodStart: string;
  /** ISO 8601 end of the current billing period. */
  periodEnd: string;
  maxFileSeconds: number | null;
  /** Null when the plan has no session throttle. */
  session: TranscriptionSessionSnapshot | null;
}

/**
 * Wire → domain, exported because `GET /rest/v1/usage` is not the only thing that answers with a
 * usage block: the 402 and the session 429 from `/transcribe` both carry a freshly computed one, so
 * a rejection can correct the header without a second round trip.
 */
export function toUsageSnapshot(usage: TranscriptionUsage): UsageSnapshot {
  return {
    plan: usage.plan,
    secondsUsed: usage.seconds_used,
    secondsLimit: usage.seconds_limit,
    secondsRemaining: usage.seconds_remaining,
    periodStart: usage.period_start,
    periodEnd: usage.period_end,
    maxFileSeconds: usage.max_file_seconds,
    session: usage.session
      ? {
        isActive: usage.session.active,
        secondsUsed: usage.session.seconds_used,
        secondsLimit: usage.session.seconds_limit,
        secondsRemaining: usage.session.seconds_remaining,
        durationSeconds: usage.session.duration_seconds,
        startedAt: usage.session.started_at,
        expiresAt: usage.session.expires_at,
      }
      : null,
  };
}

export const fetchTranscriptionUsage = createAsyncThunk<
  UsageSnapshot,
  void,
  {rejectValue: ApiError}
>(
  'usage/fetchTranscriptionUsage',
  async (_, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const response = await apiFetch(`${getWorkerApiUrl()}/rest/v1/usage`, {
        headers: {Authorization: `Bearer ${jwt}`},
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        return rejectWithValue(toApiError(response.status, errorText, 'Could not read your usage'));
      }

      const data: UsageResponse = await response.json();
      const usage = data.transcription;

      debugLog('[Usage] Transcription seconds used this period:', usage.seconds_used);

      return toUsageSnapshot(usage);
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(toNetworkError(error, 'Could not read your usage'));
    }
  }
);
